/**
*
* RequestItem
*
*/

import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, ListGroupItem } from 'react-bootstrap';
import classNames from 'classnames';
import useDialog from 'containers/Dialog/useDialog.js';
import AvatarWithText from 'components/media/AvatarWithText/index.jsx';
import Icon2 from 'components/media/Icon2/index.js';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import FormattedDate from 'components/FormattedDate/index.jsx';
import { format } from 'utils/date-fns/index.js';
import messages from './messages.js';
import { EMPLOYMENT_ENTREPRENEUR, EMPLOYMENT_EMPLOYED, CAN_DELETE_STATUS, CAN_EDIT_STATUS, LEAVESTATUS_ACCEPTED, LEAVESTATUS_REJECTED, LEAVESTATUS_PROPS } from './constants.js';
import asString from './RequestForm/CompanyInfo/asString.js';

const InfoList = styled.ul.attrs({ className: 'list-unstyled form-text' })`
  li {
    margin-top: 5px;
    &.with-border {
      border-left: 2px solid ${(p) => p.brandSuccess};
      padding-left: 5px;
      &.text-danger {
        border-color: ${(p) => p.brandDanger};
      }
    }
  }
`;

function RequestItem(props) {
  const { item, onChange, onClick, expanded, types } = props;
  const [deleted, setDeleted] = useState(false);
  const { dlgClick, REMOVE_OPTIONS } = useDialog();

  function revokeRequest() {
    setDeleted(true);
    return onChange({
      method: 'DELETE',
      id: item.id,
      body: {},
      error: { 403: messages.couldNotDelete },
    });
  }

  function renderEmployment() {
    switch (item.employmentType) {
      case EMPLOYMENT_EMPLOYED:
        return (
          <>
            <span className="with-colon"><FormattedMessage {...messages.emplEmployed} /></span>
            {item.document && <a href={item.document.fullPath}>{item.document.fileName}</a>}
          </>
        );
      case EMPLOYMENT_ENTREPRENEUR:
        return (
          <>
            <span className="with-colon"><FormattedMessage {...messages.emplEntrepreneur} /></span>
            {asString(item.companyInfo)}
          </>
        );
      default:
        return null;
    }
  }

  const infoClassName = classNames([
    { 'with-border': !item.statusBy },
    { 'text-success': item.status === LEAVESTATUS_ACCEPTED },
    { 'text-danger': item.status === LEAVESTATUS_REJECTED },
  ]);

  const classList = classNames(['d-flex align-items-baseline', { 'opacity-50': deleted }]);
  const icon = CAN_EDIT_STATUS.includes(item.status) ? 'ss-draw' : 'ss-info';
  const { style, message } = LEAVESTATUS_PROPS[item.status];
  const handleClick = () => dlgClick({ title: messages.revokeRequest, ...REMOVE_OPTIONS, confirmLabel: messages.revoke }, revokeRequest);
  return (
    <ListGroupItem key={item.id}>
      <div className={classList}>
        <div className="flex-grow-1">
          <div className="request-type">{types[item.reportType].description}</div>
          <FormattedDate className="small text-muted" format="LL">{{ ...item, allDay: true }}</FormattedDate>
        </div>
        {CAN_DELETE_STATUS.includes(item.status) && (
          <DeleteButton
            icon="ss-delete fs-3 lower-1"
            variant="link"
            className="opacity-50"
            aria-label="Delete/Revoke request"
            onClick={handleClick}
            disabled={deleted}
          />
        )}
        <Button variant={style} size="sm py-1" onClick={() => onClick(item)}>
          <FormattedMessage {...message} />
          <span className="within-parentheses ms-2">{format(item.statusDate, 'PP')}</span>
          {icon && <Icon2 className={`ms-3 fs-4 lower-1 ${icon}`} />}
        </Button>
      </div>
      { expanded && (
        <InfoList>
          {item.statusBy ? (
            <li className={infoClassName}>
              <AvatarWithText src={item.statusBy.avatar} bsSize="small">
                <span className="status-explanation">{item.statusExplanation || ''}</span>
                <div className="fst-italic">{item.statusBy.displayName}</div>
              </AvatarWithText>
            </li>
          ) : (
            item.statusExplanation && <li className={infoClassName}>{item.statusExplanation}</li>
          )}
          {item.explanation && <li>{item.explanation}</li>}
          {item.lateExplanation && (
            <li>
              <span className="with-colon"><FormattedMessage {...messages.lateBecause} /></span>
              {item.lateExplanation}
            </li>
          )}
          {item.employmentType > 0 && <li>{renderEmployment()}</li>}
        </InfoList>
      )}
    </ListGroupItem>
  );
}

RequestItem.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  types: PropTypes.object,
  expanded: PropTypes.bool,
  onChange: PropTypes.func,
};

export default RequestItem;
